import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AuditLogTimeline,
  Button,
  Card,
  Hairline,
  Numeric,
  StatusPill,
  Tab,
  TabPanel,
  Tabs,
  TabsList,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  type Tone,
} from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { formatGBP, formatTimestamp } from "../lib/format.js";
import { useToast } from "../lib/toast.js";
import { CancelOrderModal } from "../components/CancelOrderModal.js";
import { CodCollectedModal } from "../components/CodCollectedModal.js";
import { CodRefusedModal } from "../components/CodRefusedModal.js";
import { DispatchModal } from "../components/DispatchModal.js";
import { FulfilOrderModal } from "../components/FulfilOrderModal.js";
import { ReceivePoModal } from "../components/ReceivePoModal.js";
import { RefundModal } from "../components/RefundModal.js";
import { IssueCashbackModal } from "../components/orders/IssueCashbackModal.js";
import {
  canDispatch,
  canMarkCollected,
  canMarkRefused,
  isCodOrder,
  orderStatusTone,
} from "../lib/cod-helpers.js";
import type {
  AdminOrderDetailDTO,
  AdminRefundStatus,
  CashbackCategory,
  RefundKind,
  ReorderResponseDTO,
  SupplierOrderSummaryDTO,
} from "@wmart/shared-types";

const REFUND_STATUS_TONE: Record<AdminRefundStatus, Tone> = {
  pending: "neutral",
  succeeded: "success",
  failed: "danger",
  cancelled: "warning",
};

/**
 * P18-T7: render the refund "Kind" column label. `standard` refunds are
 * just "Refund"; `cashback` refunds spell out the category so operators
 * can scan the timeline without expanding the row.
 */
function renderRefundKind(
  kind: RefundKind,
  category: CashbackCategory | null,
): string {
  if (kind === "cashback" && category) {
    const cap = category.charAt(0).toUpperCase() + category.slice(1);
    return `Cashback (${cap})`;
  }
  return "Refund";
}

type ModalKind =
  | "fulfil"
  | "cancel"
  | "refund"
  | "cashback"
  | "dispatch"
  | "cod_collected"
  | "cod_refused"
  | null;

export function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const { notify, notifyProblem } = useToast();
  const [order, setOrder] = useState<AdminOrderDetailDTO | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [modal, setModal] = useState<ModalKind>(null);
  const [reorderBusy, setReorderBusy] = useState<"merge" | "replace" | null>(
    null,
  );
  // Supplier P1: po.id of the PO whose resend-email call is in flight, so the
  // per-row button disables while busy (same idiom as reorderBusy).
  const [resendBusy, setResendBusy] = useState<string | null>(null);
  // Task 13 GRN: po.id of the PO for which the receive modal is open (one at a time).
  const [receivePo, setReceivePo] = useState<SupplierOrderSummaryDTO | null>(null);
  // Task 13 GRN: po.id of the PO whose needed_by patch is in flight.
  const [neededByBusy, setNeededByBusy] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!id) return;
    try {
      const data = await api.get<AdminOrderDetailDTO>(
        `/v1/admin/orders/${id}`,
      );
      setOrder(data);
      setLoadError(false);
    } catch (err) {
      setLoadError(true);
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to load order");
    }
  }, [id, notify, notifyProblem]);

  // P21-A4: reorder into the customer's cart on behalf of the customer.
  // The admin endpoint (`POST /v1/admin/orders/:id/reorder?strategy=…`)
  // returns the same ReorderResponseDTO shape as the storefront endpoint.
  // We surface a one-line toast summarising counts of adjusted/skipped/
  // price-changed lines so the operator knows the outcome at a glance.
  async function reorderOnBehalf(
    orderId: string,
    strategy: "merge" | "replace",
  ) {
    setReorderBusy(strategy);
    try {
      const data = await api.post<ReorderResponseDTO>(
        `/v1/admin/orders/${orderId}/reorder?strategy=${strategy}`,
        {},
      );
      const parts: string[] = [];
      if (data.adjustments.length > 0)
        parts.push(`${data.adjustments.length} adjusted`);
      if (data.skipped.length > 0)
        parts.push(`${data.skipped.length} skipped`);
      if (data.priceChanges.length > 0)
        parts.push(`${data.priceChanges.length} price change${data.priceChanges.length === 1 ? "" : "s"}`);
      const summary = parts.length > 0 ? ` — ${parts.join(", ")}` : "";
      notify("success", `Reordered into customer cart${summary}`);
    } catch (err) {
      if (err instanceof ApiError && err.problem) {
        notifyProblem(err.problem);
      } else {
        notify("error", "Failed to reorder into customer cart");
      }
    } finally {
      setReorderBusy(null);
    }
  }

  async function resendPoEmail(po: SupplierOrderSummaryDTO) {
    setResendBusy(po.id);
    try {
      await api.post(`/v1/admin/supplier-orders/${po.id}/resend-email`, {});
      notify("success", `PO email resent for ${po.po_number}`);
      void refetch();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to resend PO email");
    } finally {
      setResendBusy(null);
    }
  }

  // Task 13 GRN: PATCH needed_by on blur.
  async function updateNeededBy(po: SupplierOrderSummaryDTO, value: string) {
    setNeededByBusy(po.id);
    try {
      await api.patch(`/v1/admin/supplier-orders/${po.id}`, {
        needed_by: value || null,
      });
      notify("success", `Needed-by updated for ${po.po_number}`);
      void refetch();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to update needed-by");
    } finally {
      setNeededByBusy(null);
    }
  }

  useEffect(() => {
    void refetch();
  }, [refetch]);

  if (loadError && !order) {
    return (
      <div className="flex flex-col gap-4">
        <Link to="/orders" className="text-body-sm text-accent hover:underline">
          ← Back to orders
        </Link>
        <p className="text-body-sm text-danger">Order could not be loaded.</p>
      </div>
    );
  }

  if (!order) {
    return <p className="text-body-sm text-ink-500">Loading…</p>;
  }

  // Status-conditional action buttons per Plan 08 T13 §1997-2001 +
  // P17-T11 COD lifecycle additions (gated via cod-helpers per L26):
  //   placed + cod       → Dispatch for delivery
  //   out_for_delivery   → Mark cash collected (primary) + Mark refused (danger)
  //   paid               → Mark fulfilled + Cancel + Refund
  //   fulfilled          → Refund only
  //   partially_refunded → Refund only
  //   cancelled / refunded / pending_payment / placed (non-cod) → no actions
  const showDispatchBtn = canDispatch(order);
  const showCodCollectedBtn = canMarkCollected(order);
  const showCodRefusedBtn = canMarkRefused(order);
  const showFulfilBtn = order.status === "paid";
  const showCancelBtn = order.status === "paid";
  const showRefundBtn =
    order.status === "paid" ||
    order.status === "fulfilled" ||
    order.status === "partially_refunded";
  // P18-T7: cashback button mirrors refund-button visibility (any paid /
  // fulfilled / partially-refunded order with remaining balance) but is
  // suppressed for COD orders — cashback is bank-credit only, COD has no
  // captured Stripe payment to offset and the spec gates COD cashback
  // separately (§7 deferred to V1.6).
  const remainingMinor = order.total_minor - order.refunded_minor;
  const showCashbackBtn = showRefundBtn && !isCodOrder(order) && remainingMinor > 0;
  const hasAnyAction =
    showDispatchBtn ||
    showCodCollectedBtn ||
    showCodRefusedBtn ||
    showFulfilBtn ||
    showCancelBtn ||
    showRefundBtn ||
    showCashbackBtn;

  const customerLabel =
    order.customer_company_name ?? order.customer_email ?? "(no email)";

  return (
    <div className="flex flex-col gap-6">
      <Link to="/orders" className="text-body-sm text-accent hover:underline">
        ← Back to orders
      </Link>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <section className="flex min-w-0 flex-col gap-6">
          <Card className="p-6">
            <div className="flex flex-wrap items-baseline justify-between gap-4">
              <div className="min-w-0">
                <h2 className="font-serif text-display-sm text-ink-900">
                  Order <span className="font-mono">{order.id.slice(0, 8)}…</span>
                </h2>
                <p className="mt-1 text-body-sm text-ink-500">
                  <Link
                    to={`/customers/${order.customer_id}`}
                    className="text-ink-700 hover:underline"
                  >
                    {customerLabel}
                  </Link>
                  {" · placed "}
                  {formatTimestamp(order.placed_at)}
                  {order.fulfilled_at ? (
                    <> · fulfilled {formatTimestamp(order.fulfilled_at)}</>
                  ) : null}
                </p>
              </div>
              <StatusPill tone={orderStatusTone(order.status)}>
                {order.status.replace(/_/g, " ")}
              </StatusPill>
              {isCodOrder(order) ? (
                <StatusPill tone="info">COD</StatusPill>
              ) : null}
            </div>
          </Card>

          <Tabs defaultValue="items">
            <TabsList aria-label="Order detail sections">
              <Tab value="items">Items</Tab>
              <Tab value="payments">Payments</Tab>
              <Tab value="refunds">Refunds</Tab>
              <Tab value="events">Events</Tab>
            </TabsList>
            <TabPanel value="items">
              {order.items.length === 0 ? (
                <p className="text-body-sm text-ink-500">No items.</p>
              ) : (
                <Table>
                  <THead>
                    <TR>
                      <TH>SKU</TH>
                      <TH>Title</TH>
                      <TH className="text-right">Qty</TH>
                      <TH className="text-right">Unit</TH>
                      <TH className="text-right">Line</TH>
                      <TH className="text-right">VAT</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {order.items.map((it) => (
                      <TR key={it.id}>
                        <TD className="font-mono text-body-sm">{it.sku_snapshot}</TD>
                        <TD>{it.title_snapshot}</TD>
                        <TD className="text-right">
                          <Numeric>{it.quantity}</Numeric>
                        </TD>
                        <TD className="text-right">
                          <Numeric serif>{formatGBP(it.unit_price_minor)}</Numeric>
                        </TD>
                        <TD className="text-right">
                          <Numeric serif>
                            {formatGBP(it.unit_price_minor * it.quantity)}
                          </Numeric>
                        </TD>
                        <TD className="text-right">
                          <Numeric>{formatGBP(it.vat_amount_minor)}</Numeric>
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              )}
            </TabPanel>
            <TabPanel value="payments">
              {order.payments.length === 0 ? (
                <p className="text-body-sm text-ink-500">No payments recorded.</p>
              ) : (
                <Table>
                  <THead>
                    <TR>
                      <TH>Method</TH>
                      <TH className="text-right">Amount</TH>
                      <TH>Received</TH>
                      <TH>Processor reference</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {order.payments.map((p) => (
                      <TR key={p.id}>
                        <TD>{p.method}</TD>
                        <TD className="text-right">
                          <Numeric serif>{formatGBP(p.amount_minor)}</Numeric>
                        </TD>
                        <TD className="text-body-sm text-ink-500">
                          {formatTimestamp(p.received_at)}
                        </TD>
                        <TD className="font-mono text-body-sm">
                          {p.processor_reference ?? "—"}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              )}
            </TabPanel>
            <TabPanel value="refunds">
              {order.refunds.length === 0 ? (
                <p className="text-body-sm text-ink-500">No refunds.</p>
              ) : (
                <Table>
                  <THead>
                    <TR>
                      <TH>Kind</TH>
                      <TH className="text-right">Amount</TH>
                      <TH>Reason</TH>
                      <TH>Status</TH>
                      <TH>Issued</TH>
                      <TH>Processor refund</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {order.refunds.map((r) => (
                      <TR key={r.id}>
                        <TD>{renderRefundKind(r.kind, r.cashback_category)}</TD>
                        <TD className="text-right">
                          <Numeric serif>{formatGBP(r.amount_minor)}</Numeric>
                        </TD>
                        <TD>{r.reason}</TD>
                        <TD>
                          <StatusPill tone={REFUND_STATUS_TONE[r.status]}>
                            {r.status}
                          </StatusPill>
                          {r.status === "failed" && r.failure_message ? (
                            <div className="mt-1 text-caption text-danger">
                              {r.failure_message}
                            </div>
                          ) : null}
                        </TD>
                        <TD className="text-body-sm text-ink-500">
                          {formatTimestamp(r.issued_at)}
                        </TD>
                        <TD className="font-mono text-body-sm">
                          {r.processor_refund_id ?? "—"}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              )}
            </TabPanel>
            <TabPanel value="events">
              {order.events.length === 0 ? (
                <p className="text-body-sm text-ink-500">No events.</p>
              ) : (
                <AuditLogTimeline
                  events={order.events.map((ev) => ({
                    id: ev.id,
                    at: ev.occurred_at,
                    title: ev.event_type.replace(/_/g, " "),
                    body: (
                      <pre className="mt-1 overflow-x-auto rounded-sm bg-paper-warm p-2 text-caption">
                        {JSON.stringify(ev.payload, null, 2)}
                      </pre>
                    ),
                    actor: `${ev.actor_kind}${
                      ev.actor_id ? ` · ${ev.actor_id.slice(0, 8)}…` : ""
                    }`,
                  }))}
                />
              )}
            </TabPanel>
          </Tabs>

          {/* Supplier purchase orders — rendered when any POs exist */}
          {order.supplier_orders.length > 0 ? (
            <Card className="p-6">
              <h3 className="mb-4 font-serif text-title-md text-ink-900">
                Purchase orders
              </h3>
              <div className="flex flex-col divide-y divide-ink-100">
                {order.supplier_orders.map((po) => {
                  const isTerminal =
                    po.status === "cancelled" || po.status === "closed";
                  const canResend = !isTerminal;
                  const canReceive =
                    po.fulfilment_mode === "depot" && po.status === "ready";

                  return (
                    <div key={po.id} className="flex flex-col gap-2 py-3">
                      {/* Row 1: PO number + supplier + email timestamp + action buttons */}
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <p className="font-mono text-body-sm font-medium text-ink-900">
                            {po.po_number}
                          </p>
                          <p className="text-body-sm text-ink-500">
                            {po.supplier_name} · {po.item_count} line
                            {po.item_count === 1 ? "" : "s"} ·{" "}
                            {po.status.replace(/_/g, " ")}
                          </p>
                        </div>
                        <span className="text-caption text-ink-500">
                          {po.emailed_at
                            ? `emailed ${formatTimestamp(po.emailed_at)}`
                            : "not emailed"}
                        </span>
                        {canReceive ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setReceivePo(po)}
                          >
                            Receive
                          </Button>
                        ) : null}
                        {canResend ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            loading={resendBusy === po.id}
                            disabled={resendBusy !== null}
                            onClick={() => void resendPoEmail(po)}
                          >
                            Resend email
                          </Button>
                        ) : null}
                      </div>

                      {/* Row 2 (Task 13): needed_by date input for non-terminal POs */}
                      {!isTerminal ? (
                        <div className="flex items-center gap-2 text-body-sm">
                          <label
                            className="text-ink-500"
                            htmlFor={`needed_by_${po.id}`}
                          >
                            Needed by
                          </label>
                          <input
                            id={`needed_by_${po.id}`}
                            type="date"
                            defaultValue={po.needed_by ?? ""}
                            disabled={neededByBusy === po.id}
                            className="rounded-sm border border-ink-200 px-2 py-0.5 text-body-sm focus:outline-none focus:ring-1 focus:ring-accent"
                            onBlur={(e) => {
                              if (e.target.value !== (po.needed_by ?? "")) {
                                void updateNeededBy(po, e.target.value);
                              }
                            }}
                          />
                        </div>
                      ) : null}

                      {/* Row 3 (Task 13): lifecycle state display */}
                      {po.status === "received" && po.received_at ? (
                        <div className="flex flex-wrap items-start gap-2 text-body-sm">
                          <span className="text-ink-600">
                            Received {formatTimestamp(po.received_at)}
                          </span>
                          {po.received_with_issues ? (
                            <StatusPill tone="warning">Issues</StatusPill>
                          ) : null}
                          {po.receipt_note ? (
                            <span className="text-ink-500">{po.receipt_note}</span>
                          ) : null}
                        </div>
                      ) : null}
                      {po.status === "shipped" && po.shipped_at ? (
                        <div className="flex flex-wrap items-start gap-2 text-body-sm">
                          <span className="text-ink-600">
                            Shipped {formatTimestamp(po.shipped_at)}
                          </span>
                          {po.tracking_note ? (
                            <span className="text-ink-500">{po.tracking_note}</span>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              {order.unassigned_item_count > 0 ? (
                <p className="mt-3 text-body-sm text-ink-500">
                  {order.unassigned_item_count} line
                  {order.unassigned_item_count === 1 ? "" : "s"} unassigned —
                  WMart own stock.
                </p>
              ) : null}
            </Card>
          ) : null}
        </section>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <Card className="flex flex-col gap-3 p-6">
            <h3 className="font-serif text-title-md text-ink-900">Actions</h3>
            {showDispatchBtn ? (
              <Button onClick={() => setModal("dispatch")}>
                Dispatch for delivery
              </Button>
            ) : null}
            {showCodCollectedBtn ? (
              <Button onClick={() => setModal("cod_collected")}>
                Mark cash collected
              </Button>
            ) : null}
            {showCodRefusedBtn ? (
              <Button
                variant="secondary"
                onClick={() => setModal("cod_refused")}
              >
                Mark COD refused
              </Button>
            ) : null}
            {showFulfilBtn ? (
              <Button onClick={() => setModal("fulfil")}>Mark fulfilled</Button>
            ) : null}
            {showCancelBtn ? (
              <Button variant="secondary" onClick={() => setModal("cancel")}>
                Cancel order
              </Button>
            ) : null}
            {showRefundBtn ? (
              <Button variant="danger" onClick={() => setModal("refund")}>
                Refund
              </Button>
            ) : null}
            {showCashbackBtn ? (
              <Button
                variant="secondary"
                onClick={() => setModal("cashback")}
              >
                Issue cashback
              </Button>
            ) : null}
            {!hasAnyAction ? (
              <p className="text-body-sm text-ink-500">
                No actions available in this state.
              </p>
            ) : null}

            {/* P21-A4: Reorder on behalf of customer — always available.
                Server enforces admin/sales role; UI shows without status
                gating so operators can trigger reorder from any order state. */}
            <Hairline spacing="sm" />
            <p className="text-overline font-semibold uppercase tracking-[0.014em] text-ink-500">
              Reorder on behalf
            </p>
            <Button
              variant="secondary"
              size="sm"
              loading={reorderBusy === "merge"}
              disabled={reorderBusy !== null}
              onClick={() => void reorderOnBehalf(order.id, "merge")}
            >
              Reorder (merge)
            </Button>
            <Button
              variant="secondary"
              size="sm"
              loading={reorderBusy === "replace"}
              disabled={reorderBusy !== null}
              onClick={() => void reorderOnBehalf(order.id, "replace")}
            >
              Reorder (replace)
            </Button>

            {/* P17 — COD lifecycle card: shown for COD orders to surface
                driver attribution + cash-receipt timestamps + cancellation
                reason. Each field renders only when populated, so the card
                grows progressively across the placed → out_for_delivery →
                paid lifecycle. */}
            {isCodOrder(order) &&
            (order.dispatched_at ||
              order.driver_staff_id ||
              order.cod_collected_at ||
              order.cod_marked_paid_by_staff_id ||
              order.cancellation_reason) ? (
              <>
                <Hairline spacing="sm" />
                <p className="text-overline font-semibold uppercase tracking-[0.014em] text-ink-500">
                  COD lifecycle
                </p>
                <dl className="flex flex-col gap-1 text-body-sm">
                  {order.dispatched_at ? (
                    <div className="flex justify-between gap-2">
                      <dt className="text-ink-500">Dispatched</dt>
                      <dd>{formatTimestamp(order.dispatched_at)}</dd>
                    </div>
                  ) : null}
                  {order.driver_staff_id ? (
                    <div className="flex justify-between gap-2">
                      <dt className="text-ink-500">Driver</dt>
                      <dd className="font-mono">
                        {order.driver_staff_id.slice(0, 8)}…
                      </dd>
                    </div>
                  ) : null}
                  {order.cod_collected_at ? (
                    <div className="flex justify-between gap-2">
                      <dt className="text-ink-500">Cash collected</dt>
                      <dd>{formatTimestamp(order.cod_collected_at)}</dd>
                    </div>
                  ) : null}
                  {order.cod_marked_paid_by_staff_id ? (
                    <div className="flex justify-between gap-2">
                      <dt className="text-ink-500">Marked paid by</dt>
                      <dd className="font-mono">
                        {order.cod_marked_paid_by_staff_id.slice(0, 8)}…
                      </dd>
                    </div>
                  ) : null}
                  {order.cancellation_reason ? (
                    <div className="flex justify-between gap-2">
                      <dt className="text-ink-500">Cancel reason</dt>
                      <dd>{order.cancellation_reason.replace(/_/g, " ")}</dd>
                    </div>
                  ) : null}
                </dl>
              </>
            ) : null}

            <Hairline spacing="sm" />

            <p className="text-overline font-semibold uppercase tracking-[0.014em] text-ink-500">
              Totals
            </p>
            <dl className="flex flex-col gap-1 text-body-sm">
              <div className="flex justify-between">
                <dt>Subtotal</dt>
                <dd>
                  <Numeric>{formatGBP(order.subtotal_minor)}</Numeric>
                </dd>
              </div>
              <div className="flex justify-between">
                <dt>VAT</dt>
                <dd>
                  <Numeric>{formatGBP(order.vat_minor)}</Numeric>
                </dd>
              </div>
              <div className="flex justify-between font-medium text-ink-900">
                <dt>Total</dt>
                <dd>
                  <Numeric serif>{formatGBP(order.total_minor)}</Numeric>
                </dd>
              </div>
              {order.refunded_minor > 0 ? (
                <div className="flex justify-between text-danger">
                  <dt>Refunded</dt>
                  <dd>
                    <span aria-hidden>−</span>
                    <Numeric>{formatGBP(order.refunded_minor)}</Numeric>
                  </dd>
                </div>
              ) : null}
            </dl>
          </Card>
        </aside>
      </div>

      {modal === "fulfil" ? (
        <FulfilOrderModal
          order={order}
          onSuccess={refetch}
          onClose={() => setModal(null)}
        />
      ) : null}
      {modal === "cancel" ? (
        <CancelOrderModal
          order={order}
          onSuccess={refetch}
          onClose={() => setModal(null)}
        />
      ) : null}
      {modal === "refund" ? (
        <RefundModal
          order={order}
          onSuccess={refetch}
          onClose={() => setModal(null)}
        />
      ) : null}
      {modal === "cashback" ? (
        <IssueCashbackModal
          open
          orderId={order.id}
          orderNumber={order.id}
          orderTotalMinor={order.total_minor}
          refundedMinor={order.refunded_minor}
          currency="GBP"
          onClose={() => setModal(null)}
          onSubmit={async (body) => {
            try {
              await api.post(
                `/v1/admin/orders/${order.id}/cashbacks`,
                body,
              );
              notify("success", "Cashback issued");
              await refetch();
            } catch (err) {
              if (err instanceof ApiError && err.problem) {
                notifyProblem(err.problem);
              } else {
                notify("error", "Failed to issue cashback");
              }
              throw err;
            }
          }}
        />
      ) : null}
      {modal === "dispatch" ? (
        <DispatchModal
          order={order}
          onSuccess={refetch}
          onClose={() => setModal(null)}
        />
      ) : null}
      {modal === "cod_collected" ? (
        <CodCollectedModal
          order={order}
          onSuccess={refetch}
          onClose={() => setModal(null)}
        />
      ) : null}
      {modal === "cod_refused" ? (
        <CodRefusedModal
          order={order}
          onSuccess={refetch}
          onClose={() => setModal(null)}
        />
      ) : null}
      {/* Task 13 GRN: receive modal — opened per-PO from the panel */}
      {receivePo ? (
        <ReceivePoModal
          po={receivePo}
          onSuccess={refetch}
          onClose={() => setReceivePo(null)}
        />
      ) : null}
    </div>
  );
}
